import {
  millisecondsInDay,
  millisecondsInMinute,
  millisecondsInSecond,
  millisecondsInWeek,
} from 'date-fns/constants';
import {
  ChartingLibraryWidgetOptions,
  IBasicDataFeed,
  IChartWidgetApi,
  IExecutionLineAdapter,
  IOrderLineAdapter,
  IPositionLineAdapter,
  LanguageCode,
  ResolutionString,
} from '../charting_library/charting_library';
import { useTradeFormStore } from '../components/TradePage/TradeForm/TradeFormState';
import tvChartTheme from '../components/TradingViewChart/tvChartTheme.css?url';
import { ORDER_STOP_TYPES } from '../constants';
import {
  LINE_LENGTH,
  OFFSET_SPACING,
  SUPPORTED_TIMEZONES,
  VOLUME_BASED_INDICATORS,
} from '../constants/tradingview';
import { Fill } from '../hooks/useFills';
import { Order } from '../hooks/useOrders';
import { Position } from '../hooks/usePositions';
import { OrderSide } from '../types/generated';
import { apiTVBaseUrl, tvUpdateFrequency } from './config';
import { captureError } from './errorTracking';
import { formatDollar, formatNumber } from './formatters';
import { Gwei } from './gwei';
import { LocalStorageChartAdapter } from './LocalStorageChartAdapter';
import { alignToValidSize, divide, getDecimalPlaces, multiply } from './math';
import { tryCatch } from './tryCatch';
import { PrettyMerge } from './tsUtilityTypes';
import { notNill } from './typeGuards';

export type ChartPriceType = 'last-price' | 'oracle-price';

export const DEFAULT_CHART_PRICE_TYPE: ChartPriceType = 'oracle-price';

// If the user has a large screen and 1min time frame is selected, and zoomed out, TradingView will request 3k+ history bars.
// They have this configuration to work around APIs with smaller limits.
// BUT, for this to work the backend needs to allow sending larger limits than 1000, but just return 1000 bars (our API would return 400 when requesting incorrect limit).
// Then the library will handle the "pagination" automatically.
// Leaving it here for reference, I think the end solution will be to write a custom UDF data loader.
// For now the backend will increase the limit to 4000, which should cover most screens.
type LimitedResponseConfiguration = {
  /**
   * Set this value to the maximum number of bars which
   * the data backend server can supply in a single response.
   * This doesn't affect or change the library behavior regarding
   * how many bars it will request. It just allows this Datafeed
   * implementation to correctly handle this situation.
   */
  maxResponseLength: number;
  /**
   * If the server can't return all the required bars in a single
   * response then `expectedOrder` specifies whether the server
   * will send the latest (newest) or earliest (older) data first.
   */
  expectedOrder: 'latestFirst' | 'earliestFirst';
};
// Declare Datafeeds on `window`, added by TradingView charting library
declare global {
  interface Window {
    Datafeeds: {
      UDFCompatibleDatafeed: {
        new (
          datafeedURL: string,
          updateFrequency?: number,
          serverResponseConfig?: LimitedResponseConfiguration
        ): IBasicDataFeed;
      };
    };
  }
}

type AveragedFill = PrettyMerge<Fill, { numFills: number }>;

const getUserTimezone = () => {
  const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return SUPPORTED_TIMEZONES.find((tz) => userTz === tz) || 'Etc/UTC';
};

const getLanguageFromURL = (): LanguageCode | null => {
  const regex = new RegExp('[\\?&]lang=([^&#]*)');
  const results = regex.exec(location.search);
  return results === null
    ? null
    : (decodeURIComponent(results[1]!.replace(/\+/g, ' ')) as LanguageCode);
};

export const createTradingViewConfig = (
  priceType: ChartPriceType = DEFAULT_CHART_PRICE_TYPE,
  useArchiveDb?: boolean
): Omit<ChartingLibraryWidgetOptions, 'interval'> => {
  const cssVars = getComputedStyle(document.documentElement);
  const green = cssVars.getPropertyValue('--background-backgroundGreen');
  const red = cssVars.getPropertyValue('--background-backgroundRed');
  const backgroundPrimary = cssVars.getPropertyValue('--background-backgroundPrimary');
  const contentSecondary = cssVars.getPropertyValue('--content-contentSecondary');
  const datafeedUrl = new URL(
    useArchiveDb ? `/v1/${priceType}` : 'https://benchmarks.pyth.network/v1/shims/tradingview',
    apiTVBaseUrl
  ).toString();

  return {
    datafeed: new window.Datafeeds.UDFCompatibleDatafeed(datafeedUrl, tvUpdateFrequency),
    library_path: '/charting_library/',
    fullscreen: false,
    autosize: true,
    favorites: {
      intervals: ['5', '60', 'D'] as ResolutionString[],
    },
    studies_overrides: {
      'volume.volume.color.0': green, // Green for up volume bars
      'volume.volume.color.1': red, // Red for down volume bars
    },
    theme: 'dark',
    loading_screen: {
      backgroundColor: backgroundPrimary,
      foregroundColor: backgroundPrimary,
    },
    custom_css_url: tvChartTheme,
    container: 'tv_chart_container',
    locale: getLanguageFromURL() || 'en',
    custom_formatters: useArchiveDb
      ? undefined
      : {
          priceFormatterFactory: (_symbolInfo, minTick = '') => {
            return {
              format: (price, signPositive) => {
                // TODO remove once we have our own data feed
                // minTick format:
                //priceScale,minMove,fracScale
                //10000,0.01,1
                const [_priceScale, tickSize, _frac] = minTick.split(',');
                const tickSizeBigint = tickSize
                  ? Gwei.toGwei(tickSize)
                  : useTradeFormStore.getState().tickSize;

                const priceDecimals = tickSizeBigint ? getDecimalPlaces(tickSizeBigint) : 2;
                const formatted = formatNumber(Gwei.toGwei(price), {
                  minimumFractionDigits: priceDecimals,
                  maximumFractionDigits: priceDecimals,
                });
                // Add positive sign if requested and price is positive
                return (signPositive && price > 0 ? '+' : '') + formatted;
              },
            };
          },
        },
    timezone: getUserTimezone(),
    custom_font_family: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
    auto_save_delay: 1,
    save_load_adapter: new LocalStorageChartAdapter(),
    load_last_chart: true,
    disabled_features: (
      [
        'header_widget', // Disable chart header in favor of custom header
        'header_symbol_search',
        'symbol_search_hot_key',
        'symbol_info', // Disables symbol info dialog
        'display_market_status', // Hides market status
        'show_symbol_logo_in_legend', // Hides symbol logo
        'show_symbol_logos',
        'header_quick_search',
        'go_to_date',
        'header_compare',
        'header_undo_redo',
        'header_saveload',
        'border_around_the_chart',
        'create_volume_indicator_by_default_once',
        'header_settings', // Hide settings icon
        'header_screenshot', // Hide screenshot icon
        'timeframes_toolbar', // Disable bottom resolution bar, including clock and log/auto
        !useArchiveDb || priceType === 'oracle-price'
          ? 'create_volume_indicator_by_default'
          : undefined,
        'use_localstorage_for_settings',
      ] as const
    ).filter(notNill),
    enabled_features: [
      'header_fullscreen_button',
      'side_toolbar_in_fullscreen_mode',
      'header_quick_search',
      'items_favoriting',
      'timezone_menu',
      'study_templates',
    ],
    overrides: {
      'paneProperties.background': backgroundPrimary,
      'paneProperties.backgroundType': 'solid',
      'paneProperties.vertGridProperties.color': 'rgba(255, 255, 255, 0.05)',
      'paneProperties.horzGridProperties.color': 'rgba(255, 255, 255, 0.05)',
      'scalesProperties.lineColor': 'rgba(255, 255, 255, 0.05)',
      'scalesProperties.textColor': contentSecondary, // Also sets legend test color
      'paneProperties.crossHairProperties.color': 'rgba(255, 255, 255, 0.5)',
      'mainSeriesProperties.candleStyle.upColor': green, // Correct green for up candles
      'mainSeriesProperties.candleStyle.downColor': red, // Correct red for down candles
      'mainSeriesProperties.candleStyle.borderUpColor': green, // Correct green border for up candles
      'mainSeriesProperties.candleStyle.borderDownColor': red, // Correct red border for down candles
      'mainSeriesProperties.candleStyle.wickUpColor': green, // Correct green wick for up candles
      'mainSeriesProperties.candleStyle.wickDownColor': red, // Correct red wick for down candles
    },
    studies_access:
      !useArchiveDb || priceType === 'oracle-price'
        ? {
            type: 'black',
            tools: VOLUME_BASED_INDICATORS.map((name) => ({ name, grayed: true })),
          }
        : undefined,
  };
};

const getCssVar = (name: string) => {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
};

export const renderFills = (
  chart: IChartWidgetApi,
  fills: Fill[],
  fillsMap: Map<string, IExecutionLineAdapter>,
  productDecimals: number,
  priceDecimals: number,
  tickSize: bigint,
  showFillsSetting: boolean
) => {
  const renderFill = async (fill: Fill | AveragedFill, isAveraged: boolean) => {
    const isBuy = fill.side === OrderSide.BUY;
    const color = isBuy
      ? getCssVar('--background-backgroundGreen')
      : getCssVar('--background-backgroundRed');
    const priceFormatted = Number(Gwei.fmt(fill.price));
    const priceDollarFormatted = formatDollar(fill.price, {
      minimumFractionDigits: priceDecimals,
      maximumFractionDigits: priceDecimals,
    });
    const sizeFormatted = formatNumber(fill.filled, {
      minimumFractionDigits: productDecimals,
      maximumFractionDigits: productDecimals,
    });
    const timestampSec = Math.floor(fill.createdAt / 1000);
    const numFills = 'numFills' in fill ? fill.numFills : 1;
    const tooltip = isAveraged
      ? `${numFills} ${isBuy ? 'Buys' : 'Sells'} ${sizeFormatted} @ ${priceDollarFormatted} (Avg)`
      : `${numFills} ${isBuy ? 'Buy' : 'Sell'} ${sizeFormatted} @ ${priceDollarFormatted}`;
    const line = fillsMap.get(fill.orderId) ?? chart.createExecutionShape();
    line.setTime(timestampSec);
    line.setPrice(priceFormatted);
    line.setDirection(isBuy ? 'buy' : 'sell');
    line.setArrowHeight(16);
    line.setText('');
    line.setTextColor(color);
    line.setArrowColor(color);
    line.setTooltip(tooltip);
    fillsMap.set(fill.orderId, line);
  };

  // always remove and redraw fills because the grouping can change when resolution changes, or new fills are added, etc.
  Array.from(fillsMap.entries()).forEach(([fillId, line]) => {
    line.remove();
    fillsMap.delete(fillId);
  });

  // Don't render fills if the setting is disabled
  if (!showFillsSetting) {
    return;
  }

  const chartResolution = chart.resolution();
  // Group the fills by candle
  const groupedFills = groupFillsByCandle(fills, chartResolution, tickSize);
  // Now render the fill lines
  groupedFills.singleFills.forEach((fill) => {
    renderFill(fill, false).catch(captureError);
  });
  groupedFills.averagedFills.forEach((fill) => {
    renderFill(fill, true).catch(captureError);
  });
};

/**
 * Renders order lines and a position line for the chart. Does not render a liqPrice line
 */
export const renderOrderAndPositionLines = (
  chart: IChartWidgetApi,
  orders: Order[],
  orderLinesMap: Map<string, IOrderLineAdapter>,
  stopLossOrder: Order | undefined,
  stopLossLineRef: { current: IOrderLineAdapter | undefined },
  takeProfitOrder: Order | undefined,
  takeProfitLineRef: { current: IOrderLineAdapter | undefined },
  position: Position | undefined,
  positionLineRef: { current: IPositionLineAdapter | undefined },
  productDecimals: number,
  priceDecimals: number,
  cancelOrder: (orderIds: string[]) => void,
  editOrder: (order: Order, newLimitPriceRaw: number) => void,
  onClosePositionClick: (productId: string) => void,
  showOrderInformation: boolean,
  showPositionsInformation: boolean,
  signerExpired: boolean
) => {
  const ordersByPrice = tryCatch(() =>
    renderOrderLines(
      chart,
      orders,
      orderLinesMap,
      productDecimals,
      priceDecimals,
      cancelOrder,
      editOrder,
      showOrderInformation,
      signerExpired
    )
  );

  // render Stop Loss line
  tryCatch(() =>
    renderPositionStopOrderLine(
      chart,
      showOrderInformation,
      ordersByPrice || {},
      stopLossOrder,
      stopLossLineRef,
      'stopLoss',
      cancelOrder,
      editOrder,
      priceDecimals,
      productDecimals,
      signerExpired
    )
  );
  // render Take Profit line
  tryCatch(() =>
    renderPositionStopOrderLine(
      chart,
      showOrderInformation,
      ordersByPrice || {},
      takeProfitOrder,
      takeProfitLineRef,
      'takeProfit',
      cancelOrder,
      editOrder,
      priceDecimals,
      productDecimals,
      signerExpired
    )
  );
  tryCatch(() =>
    renderPositionLine(
      chart,
      position,
      positionLineRef,
      ordersByPrice || {},
      productDecimals,
      priceDecimals,
      onClosePositionClick,
      showPositionsInformation
    )
  );
};

/**
 * Renders order lines for the chart.
 * @returns A map of orders by price.
 */
const renderOrderLines = (
  chart: IChartWidgetApi,
  orders: Order[],
  orderLinesMap: Map<string, IOrderLineAdapter>,
  productDecimals: number,
  priceDecimals: number,
  cancelOrder: (orderIds: string[]) => void,
  editOrder: (order: Order, newLimitPriceRaw: number) => void,
  showOrderInformation: boolean,
  signerExpired: boolean
): { [price: string]: Order[] } => {
  const currentOrderIds = new Set(orders?.map((order) => order.id) || []);

  // Clean up removed orders
  Array.from(orderLinesMap.entries()).forEach(([orderId, line]) => {
    if (!currentOrderIds.has(orderId)) {
      line.remove();
      orderLinesMap.delete(orderId);
    }
  });

  // Group orders by price
  const ordersByPrice = getOrdersByPrice(orders);

  // Render orders with offset for same price
  Object.entries(ordersByPrice).forEach(([_price, ordersAtPrice]) => {
    ordersAtPrice?.forEach(async (order, index) => {
      const existingLine = orderLinesMap.get(order.id);
      const offset = index * OFFSET_SPACING; // Adjust this value to control spacing between orders

      const formattedQuantity = order.close
        ? 'Close'
        : formatNumber(order.quantity, {
            minimumFractionDigits: productDecimals,
            maximumFractionDigits: productDecimals,
          });

      const stopPriceDollarFormatted = formatDollar(order.stopPrice, {
        minimumFractionDigits: priceDecimals,
        maximumFractionDigits: priceDecimals,
      });

      const text =
        order.stopPriceType === undefined
          ? `${order.type}`
          : `${order.stopType === ORDER_STOP_TYPES.GAIN ? 'TP' : 'SL'} at ${stopPriceDollarFormatted}`;

      const price = order.stopPrice || order.price;
      if (existingLine) {
        if (!showOrderInformation) {
          existingLine.remove();
          orderLinesMap.delete(order.id);
          return;
        }
        const existingPrice = Gwei.toGwei(existingLine.getPrice());

        // We have a existing order line, update rather than create new.
        if (existingPrice !== price) {
          existingLine.setPrice(Number(Gwei.fmt(order.price)));
        }

        if (existingLine.getQuantity() !== formattedQuantity) {
          existingLine.setQuantity(formattedQuantity);
        }

        // Update the line length to create offset
        existingLine.setLineLength(LINE_LENGTH + offset);
        existingLine.setText(text);
        existingLine.setEditable(!signerExpired);
      } else {
        if (!showOrderInformation) {
          return;
        }
        const line = chart.createOrderLine();
        line
          .setText(text)
          .setTooltip(
            `Drag to adjust price. ${
              order.otocoGroupId && order.stopType == undefined
                ? 'Changing this order will cancel related SL/TP orders'
                : ''
            }`
          )
          .setPrice(Number(Gwei.fmt(price)))
          .setQuantity(formattedQuantity)
          .setLineLength(LINE_LENGTH + offset)
          .onCancel(() => {
            cancelOrder([order.id]);
          })
          .setEditable(!signerExpired)
          .onMove(function (this: IOrderLineAdapter) {
            this.setEditable(false);
            this.setCancellable(false);
            this.setText('Updating...');
            editOrder(order, this.getPrice());
          })
          .setCancelTooltip('Cancel Order');

        applyOrderLineStyles(
          line,
          order.side === OrderSide.BUY
            ? getCssVar('--background-backgroundGreen')
            : getCssVar('--background-backgroundRed')
        );
        orderLinesMap.set(order.id, line);
      }
    });
  });

  return ordersByPrice;
};

/**
 * Renders a Stop Loss or Take Profit line on the chart
 */
const renderPositionStopOrderLine = (
  chart: IChartWidgetApi,
  showOrderInformation: boolean,
  ordersByPrice: { [price: string]: Order[] },
  stopOrder: Order | undefined,
  stopOrderLineRef: { current: IOrderLineAdapter | undefined },
  type: 'stopLoss' | 'takeProfit',
  cancelOrder: (orderIds: string[]) => void,
  editOrder: (order: Order, newLimitPriceRaw: number) => void,
  priceDecimals: number,
  productDecimals: number,
  signerExpired: boolean
) => {
  // Don't render lines if we don't want to show order information or there's no stop order
  if (!showOrderInformation || !stopOrder) {
    // Clean up existing line
    if (stopOrderLineRef.current) {
      stopOrderLineRef.current.remove();
      stopOrderLineRef.current = undefined;
    }
    return;
  }

  const formattedStopSize =
    stopOrder.quantity > 0n
      ? formatNumber(Gwei.abs(stopOrder.quantity), {
          minimumFractionDigits: productDecimals,
          maximumFractionDigits: productDecimals,
        })
      : '';
  const stopPrice = stopOrder.stopPrice;
  const stopPriceKey = stopPrice.toString();
  const stopPriceDollarFormatted = formatDollar(stopPrice, {
    minimumFractionDigits: priceDecimals,
    maximumFractionDigits: priceDecimals,
  });
  const numOrdersAtPrice = ordersByPrice[stopPriceKey]?.length || 0;
  const offset = numOrdersAtPrice * OFFSET_SPACING;
  const lineLength = LINE_LENGTH + offset;
  const line = stopOrderLineRef.current ?? chart.createOrderLine();
  line
    .setPrice(Number(Gwei.fmt(stopPrice)))
    .setLineLength(lineLength)
    .setText(`Pos ${type === 'takeProfit' ? 'TP' : 'SL'} > ${stopPriceDollarFormatted}`)
    .setQuantity(formattedStopSize)
    .setTooltip(
      `${type === 'takeProfit' ? 'Take Profit' : 'Stop Loss'} at ${stopPriceDollarFormatted}`
    )
    .setEditable(!signerExpired)
    .setCancellable(true)
    .onMove(function (this: IOrderLineAdapter) {
      this.setEditable(false);
      this.setCancellable(false);
      this.setText('Updating...');
      editOrder(stopOrder, this.getPrice());
    })
    .onCancel(() => {
      cancelOrder([stopOrder.id]);
    });

  applyStopOrderLineStyles(line);
  stopOrderLineRef.current = line;
  // add the stop order to list of orders at the stop price
  addToOrdersByPriceMap(stopOrder, ordersByPrice, true);
};

/**
 * Renders a position line for the chart.
 */
const renderPositionLine = (
  chart: IChartWidgetApi,
  position: Position | undefined,
  positionLineRef: { current: IPositionLineAdapter | undefined },
  ordersByPrice: { [price: string]: Order[] },
  productDecimals: number,
  priceDecimals: number,
  onClosePositionClick: (productId: string) => void,
  showPositionsInformation: boolean
) => {
  // Clean up existing position line
  if (positionLineRef.current) {
    positionLineRef.current.remove();
    positionLineRef.current = undefined;
  }

  // Don't draw a position line if there is no position
  if (!position || !showPositionsInformation) {
    return;
  }

  // calculate the offset of the position. Need to look at the number of orders at the avgPrice
  let positionOffset = 0;
  const positionPriceKey = position.avgPrice.toString();
  if (ordersByPrice[positionPriceKey]) {
    positionOffset = ordersByPrice[positionPriceKey].length * OFFSET_SPACING;
  }

  const formattedSize = formatNumber(Gwei.abs(position.size), {
    minimumFractionDigits: productDecimals,
    maximumFractionDigits: productDecimals,
  });

  const positionDirection = position.size > 0 ? 'LONG' : 'SHORT';
  const posLine = chart.createPositionLine();
  posLine
    .setText(positionDirection)
    .setTooltip(
      `${positionDirection} Position at ${formatDollar(position.avgPrice, { minimumFractionDigits: priceDecimals, maximumFractionDigits: priceDecimals })}`
    )
    .setPrice(Number(Gwei.fmt(position.avgPrice)))
    .setQuantity(formattedSize)
    .setLineLength(LINE_LENGTH + positionOffset)
    .onClose(() => {
      // Open the close position modal
      onClosePositionClick(position.productId);
    });

  applyPositionLineStyles(
    posLine,
    position.size > 0
      ? getCssVar('--background-backgroundGreen')
      : getCssVar('--background-backgroundRed')
  );
  positionLineRef.current = posLine;
};

/**
 * Renders a Liquidation Price line for the chart.
 */
export const renderLiqPriceLine = (
  chart: IChartWidgetApi,
  orders: Order[],
  stopLossOrder: Order | undefined,
  takeProfitOrder: Order | undefined,
  position: Position | undefined,
  liqPrice: bigint | undefined,
  liqPriceLineRef: { current: IPositionLineAdapter | undefined },
  priceDecimals: number,
  showLiqPrice: boolean
) => {
  // Don't draw a liqPrice line if there is no position or liqPrice
  if (!position || !liqPrice || !showLiqPrice) {
    // Clean up existing liqPrice line if necessary
    if (liqPriceLineRef.current) {
      liqPriceLineRef.current.remove();
      liqPriceLineRef.current = undefined;
    }
    return;
  }

  // calculate the offset of the liqPrice. Need to look at the number of orders at the liqPrice.
  // Note that we don't need to look at the position offset, because the position's avgPrice line
  // won't be at the same price as the position's liquidation price.
  // We also need to check if there is a stop loss or take profit order at the liqPrice.
  const ordersByPrice = getOrdersByPrice(orders);
  let liqPriceOffset = 0;
  const liqPriceKey = liqPrice.toString();
  // push the stopPrice of the stopLoss order into the ordersByPrice map
  if (stopLossOrder) {
    addToOrdersByPriceMap(stopLossOrder, ordersByPrice, true);
  }

  // push the stopPrice of the takeProfit order into the ordersByPrice map
  if (takeProfitOrder) {
    addToOrdersByPriceMap(takeProfitOrder, ordersByPrice, true);
  }

  // finally calculate the offset for the liqPrice
  if (ordersByPrice[liqPriceKey]) {
    liqPriceOffset = ordersByPrice[liqPriceKey].length * OFFSET_SPACING;
  }

  const liqPriceDollarFormatted = formatDollar(liqPrice, {
    minimumFractionDigits: priceDecimals,
    maximumFractionDigits: priceDecimals,
  });
  const liqPriceFormatted = Number(Gwei.fmt(liqPrice));
  const existingLine = liqPriceLineRef.current;
  const lineLength = LINE_LENGTH + liqPriceOffset;
  if (existingLine) {
    // update the existing line rather than creating a new one
    existingLine.setPrice(liqPriceFormatted);
    existingLine.setQuantity(liqPriceDollarFormatted);
    existingLine.setLineLength(lineLength);
  } else {
    const liqPriceLine = chart.createPositionLine();
    liqPriceLine
      .setText('Est. Liq. Price')
      .setTooltip(`Estimated Liquidation Price at ${liqPriceDollarFormatted}`)
      .setPrice(liqPriceFormatted)
      .setQuantity(liqPriceDollarFormatted)
      .setLineLength(lineLength);

    applyLiqPriceLineStyles(liqPriceLine, getCssVar('--background-backgroundRed'));
    liqPriceLineRef.current = liqPriceLine;
  }
};

const applyOrderLineStyles = (line: IOrderLineAdapter, color: string) =>
  line
    .setBodyBorderColor(getCssVar('--border-borderPrimary'))
    .setQuantityTextColor('#000')
    // .setQuantityTextColor(getCssVar('--content-contentPrimary'))
    .setQuantityBorderColor(getCssVar('--border-borderPrimary'))
    .setBodyTextColor('#000')
    // .setBodyTextColor(getCssVar('--content-contentPrimary'))
    .setLineColor(color)
    .setLineStyle(2)
    .setBodyBackgroundColor(color)
    .setQuantityBackgroundColor(color)
    .setCancelButtonIconColor(getCssVar('--border-borderPrimary'))
    .setCancelButtonBorderColor(getCssVar('--border-borderPrimary'))
    .setCancelButtonBackgroundColor(color);

const applyStopOrderLineStyles = (line: IOrderLineAdapter) => line.setLineStyle(2);

const applyPositionLineStyles = (line: IPositionLineAdapter, color: string) =>
  line
    .setBodyBorderColor(getCssVar('--border-borderPrimary'))
    .setQuantityTextColor('#000')
    // .setQuantityTextColor(getCssVar('--content-contentPrimary'))
    .setQuantityBorderColor(getCssVar('--border-borderPrimary'))
    .setBodyTextColor('#000')
    .setLineColor(color)
    .setLineStyle(0)
    .setLineWidth(0.5)
    .setBodyBackgroundColor(color)
    .setQuantityBackgroundColor(color)
    .setCloseButtonIconColor(getCssVar('--border-borderPrimary'))
    .setCloseButtonBorderColor(getCssVar('--border-borderPrimary'))
    .setCloseButtonBackgroundColor(color);

const applyLiqPriceLineStyles = (line: IPositionLineAdapter, color: string) =>
  line
    .setBodyBorderColor(getCssVar('--border-borderPrimary'))
    .setBodyTextColor('#000')
    .setLineColor(color)
    .setLineStyle(0)
    .setLineWidth(0.5)
    .setBodyBackgroundColor(color)
    .setQuantityTextColor('#000')
    .setQuantityBorderColor(getCssVar('--border-borderPrimary'))
    .setQuantityBackgroundColor(color);

const getOrdersByPrice = (orders: Order[]) => {
  return orders.reduce<{ [price: string]: Order[] }>((acc, order) => {
    addToOrdersByPriceMap(order, acc);
    return acc;
  }, {});
};

/**
 * Adds an order to the ordersByPrice map.
 * @param order - The order to add.
 * @param ordersByPrice - The map of orders by price.
 * @param useStopPrice - Whether to use the stop price of the order. If false, use the price.
 */
const addToOrdersByPriceMap = (
  order: Order,
  ordersByPrice: { [price: string]: Order[] },
  useStopPrice: boolean = false
) => {
  const priceKey = useStopPrice ? order.stopPrice.toString() : order.price.toString();
  if (!ordersByPrice[priceKey]) {
    ordersByPrice[priceKey] = [];
  }
  ordersByPrice[priceKey].push(order);
};

/**
 * Groups fills by candle (separated by side). If there is only one buy in a candle or only one sell in a candle, it is added to the singleFills array.
 * If there are multiple buys or sells in a candle, they are averaged and added to the averagedFills array.
 */
export const groupFillsByCandle = (
  fills: Fill[],
  resolution: ResolutionString,
  tickSize: bigint
): { singleFills: Fill[]; averagedFills: AveragedFill[] } => {
  // we will group the fills by candle, separating buy/sell fills
  const candleToFills: Record<string, { buys: Fill[]; sells: Fill[] }> = {};
  const resolutionMs = resolutionToMs(resolution);

  // group fills by candle
  for (const fill of fills) {
    // determine which candle the fill belongs to
    const candleStartMs = Math.floor(fill.createdAt / resolutionMs) * resolutionMs;

    if (!candleToFills[candleStartMs]) {
      candleToFills[candleStartMs] = { buys: [], sells: [] };
    }
    if (fill.side === OrderSide.BUY) {
      candleToFills[candleStartMs].buys.push(fill);
    } else {
      candleToFills[candleStartMs].sells.push(fill);
    }
  }

  // now go through the fills in each candle and:
  // - if there is only one buy or sell in that candle, put it into the singleFills array
  // - if there are multiple buys or sells, average them and put the average into the averagedFills array
  const singleFills: Fill[] = [];
  const averagedFills: AveragedFill[] = [];
  Object.entries(candleToFills).forEach(([candleStartStr, fills]) => {
    const { buys, sells } = fills;
    if (buys.length > 1) {
      averagedFills.push(calculateAverageFill(buys, Number(candleStartStr), tickSize));
    } else if (buys.length === 1 && buys[0]) {
      singleFills.push(buys[0]);
    }

    if (sells.length > 1) {
      averagedFills.push(calculateAverageFill(sells, Number(candleStartStr), tickSize));
    } else if (sells.length === 1 && sells[0]) {
      singleFills.push(sells[0]);
    }
  });

  return { singleFills, averagedFills };
};

export const calculateAverageFill = (
  fills: Fill[],
  candleStartMs: number,
  tickSize: bigint
): AveragedFill => {
  const firstFill = fills[0];
  if (!firstFill) {
    throw new Error('No fills to calculate average');
  }

  // Calculate the total size and the weighted sum of prices
  const totalSize = fills.reduce((acc, fill) => acc + fill.filled, 0n);
  const weightedPriceSum = fills.reduce((acc, fill) => acc + multiply(fill.price, fill.filled), 0n);

  // Calculate the volume-weighted average price

  const avgPrice = alignToValidSize(divide(weightedPriceSum, totalSize), tickSize);

  // we will use the orderId of the first fill to represent the average fill (necessary to keep track of this fill line so we can remove it later)
  const { orderId, side, productId } = firstFill;

  // Return a 'dummy' fill to represent the average of all fills for the candle
  return {
    price: avgPrice, // Volume-weighted average price
    filled: totalSize, // Sum of all fills for the candle
    createdAt: candleStartMs,
    side,
    feeUsd: 0n,
    reduceOnly: false,
    orderId,
    type: 'MARKET',
    isMaker: false,
    productId,
    numFills: fills.length,
  };
};

/**
 * Converts a TradingView resolution string to milliseconds.
 * ex. "1D" -> 86400000, "1W" -> 604800000, "120" (120min) -> 7200000
 */
export const resolutionToMs = (resolution: ResolutionString): number => {
  // regex to match different resolution strings
  const match = resolution.match(/^(\d+)([TMSDWM]?)?$/);

  if (!match) {
    throw new Error(`Invalid resolution format: ${resolution}`);
  }

  const amountStr = match[1];
  const amount = Number(amountStr);
  const unit = match[2] ?? ''; // default to empty string = minutes

  switch (unit) {
    case 'T': // ticks (not supported)
      throw new Error('Tick resolution is not time-based. Cannot group by timestamp.');
    case 'S': // seconds
      return amount * millisecondsInSecond;
    case '': // minutes
      return amount * millisecondsInMinute;
    case 'D': // days
      return amount * millisecondsInDay;
    case 'W': // weeks
      return amount * millisecondsInWeek;
    case 'M': // months
      // TODO confirm this as we move to Ethereal's data feed
      return amount * 30 * millisecondsInDay; // rough month
    default:
      throw new Error(`Unhandled resolution unit: ${unit}`);
  }
};
